// Printing a document the viewer is showing.
//
// `window.print()` prints OUR document, and the `@media print` block in `styles/globals.css` isolates the
// `.orion-doc-viewer-body` subtree so the app's chrome doesn't come with it. That is the right printer for
// every kind whose content is real DOM in our own document — markdown, csv, text, an image, a converted
// Office file, a wixel preview.
//
// A PDF is not: its bytes live in a separate document inside an `<embed>`, which the parent print job cannot
// paginate (it comes out blank or clipped to the on-screen box). So a view whose content the parent job
// cannot reach REGISTERS ITS OWN PRINTER here, and `printDocument()` uses it instead. One registration slot,
// because exactly one document is on screen at a time.
//
// Why a registry rather than a prop: the Print entry is built by `buildFileActions` in three places (the
// viewer footer and the two file lists' row menus), and the row menus print a document they have only just
// opened — they have no handle on the view at all. A module-level slot is the one thing all three can reach
// without every caller learning which kinds are special.

/** Prints whatever is currently on screen. */
type DocumentPrinter = () => void;

let registered: DocumentPrinter | null = null;

/** Installs `printer` as the printer for the document now on screen, and returns the un-installer. Call it
 *  from an effect so the printer dies with the view — a stale one would print the previous file.
 *
 *  A later registration REPLACES an earlier one, and un-installing only clears the slot when it is still
 *  yours. React mounts the next view before unmounting the previous one, so a naive `registered = null` on
 *  unmount would wipe the incoming view's printer. */
export function registerDocumentPrinter(printer: DocumentPrinter): () => void {
  registered = printer;
  return () => {
    if (registered === printer) registered = null;
  };
}

/** True when a view has claimed the print job. Exported for tests; call sites just call `printDocument`. */
export function hasDocumentPrinter(): boolean {
  return registered !== null;
}

/** Prints the document on screen — the registered printer when a view claimed the job, else our own page
 *  (where the `@media print` rule does the isolating). */
export function printDocument(): void {
  if (registered) {
    registered();
    return;
  }
  if (typeof window !== 'undefined') window.print();
}

/** How long the print frame stays in the DOM after the job is handed to the browser. Removing it right away
 *  cancels the job in Chrome — `print()` returns as soon as the dialog opens, not when it closes — so the
 *  frame has to outlive the dialog. A minute is the same order as `artifact-file.ts`'s blob-URL lifetime. */
const PRINT_FRAME_TTL_MS = 60_000;

/** Prints a PDF that has already been fetched into a `blob:` URL, via an off-screen frame it can paginate
 *  itself. Returns whether the print job was handed off, so a caller can fall back.
 *
 *  ⚠️ THIS FRAME IS SAME-ORIGIN, WHICH IS ONLY SAFE BECAUSE OF THE BLOB'S TYPE. A `blob:` URL inherits our
 *  origin, so a scriptable document loaded into a frame this way would have exactly the access the doc
 *  viewer's sandbox exists to deny (see `test/doc-viewer-sandbox.test.ts`). It is safe here — and ONLY here —
 *  because `fetchDocBlob` stamps `application/pdf` on the blob itself, from our own constant rather than from
 *  the response's header: the browser honours the blob's own type, hands the bytes to its PDF viewer, and
 *  never parses them as a document of ours. **Never point this at a blob whose type came off the wire, and
 *  never at an HTML one.**
 *
 *  Same-origin is the whole point: `contentWindow.print()` needs it. That is also why this cannot be reused
 *  for an HTML report — printing one means calling `print()` INSIDE the sandboxed frame, which needs
 *  `allow-modals`, which would also let an agent-authored report raise dialogs attributed to our own origin.
 *  A report's printable path stays "Open in new tab", where it is the whole document on a foreign origin and
 *  the browser's own Print works properly. */
export function printPdfObjectUrl(objectUrl: string): boolean {
  if (typeof document === 'undefined') return false;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = '';
  // Off-screen but LAID OUT — `display: none` gives the frame no layout, and a frame with no layout prints
  // blank. A4 at 96dpi, so the plugin paginates for paper rather than for a letterbox.
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;';
  frame.src = objectUrl;
  frame.addEventListener('load', () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      // A browser that refuses to print a framed PDF (WebKit, in practice) gets the file in a tab instead,
      // where its own viewer has a Print button. Better than a Print that silently does nothing.
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
    }
    window.setTimeout(() => frame.remove(), PRINT_FRAME_TTL_MS);
  });
  document.body.appendChild(frame);
  return true;
}
